import React, { useState } from 'react';

// Define types for our component
interface SqlToSqliteProps { }

// Define database types
type ColumnDefinition = {
    name: string;
    type: string;
    constraints?: string[];
};

type DatabaseTable = {
    name: string;
    columns: ColumnDefinition[];
    rows: any[][];
    primaryKey?: string;
    foreignKeys?: ForeignKeyDefinition[];
};

type ForeignKeyDefinition = {
    column: string;
    referenceTable: string;
    referenceColumn: string;
};

// Mock database for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        columns: [
            { name: 'id', type: 'number', constraints: ['PRIMARY KEY', 'AUTOINCREMENT'] },
            { name: 'name', type: 'string', constraints: ['NOT NULL'] },
            { name: 'email', type: 'string', constraints: ['NOT NULL', 'UNIQUE'] },
            { name: 'created_at', type: 'date' }
        ],
        primaryKey: 'id',
        rows: [
            [1, 'John Doe', 'john@example.com', '2023-01-15'],
            [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
            [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
            [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
            [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
        ]
    },
    products: {
        name: 'products',
        columns: [
            { name: 'id', type: 'number', constraints: ['PRIMARY KEY', 'AUTOINCREMENT'] },
            { name: 'name', type: 'string', constraints: ['NOT NULL'] },
            { name: 'price', type: 'number', constraints: ['NOT NULL'] },
            { name: 'category', type: 'string' },
            { name: 'in_stock', type: 'boolean', constraints: ['DEFAULT 1'] }
        ],
        primaryKey: 'id',
        rows: [
            [101, 'Laptop', 1299.99, 'Electronics', true],
            [102, 'Smartphone', 899.99, 'Electronics', true],
            [103, 'Headphones', 199.99, 'Electronics', false],
            [104, 'Desk Chair', 249.99, 'Furniture', true],
            [105, 'Coffee Table', 349.99, 'Furniture', true],
            [106, 'Bookshelf', 199.99, 'Furniture', false],
            [107, 'Water Bottle', 24.99, 'Kitchen', true],
            [108, 'Blender', 89.99, 'Kitchen', true]
        ]
    },
    orders: {
        name: 'orders',
        columns: [
            { name: 'id', type: 'number', constraints: ['PRIMARY KEY', 'AUTOINCREMENT'] },
            { name: 'user_id', type: 'number', constraints: ['NOT NULL'] },
            { name: 'total', type: 'number', constraints: ['NOT NULL'] },
            { name: 'status', type: 'string', constraints: ['NOT NULL', "DEFAULT 'Processing'"] },
            { name: 'order_date', type: 'date', constraints: ['NOT NULL'] }
        ],
        primaryKey: 'id',
        foreignKeys: [
            { column: 'user_id', referenceTable: 'users', referenceColumn: 'id' }
        ],
        rows: [
            [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
            [1002, 2, 899.99, 'Shipped', '2023-06-20'],
            [1003, 3, 249.99, 'Processing', '2023-06-25'],
            [1004, 1, 89.99, 'Delivered', '2023-07-01'],
            [1005, 4, 549.98, 'Shipped', '2023-07-05'],
            [1006, 5, 199.99, 'Processing', '2023-07-10']
        ]
    },
    order_items: {
        name: 'order_items',
        columns: [
            { name: 'id', type: 'number', constraints: ['PRIMARY KEY', 'AUTOINCREMENT'] },
            { name: 'order_id', type: 'number', constraints: ['NOT NULL'] },
            { name: 'product_id', type: 'number', constraints: ['NOT NULL'] },
            { name: 'quantity', type: 'number', constraints: ['NOT NULL', 'DEFAULT 1'] },
            { name: 'unit_price', type: 'number', constraints: ['NOT NULL'] }
        ],
        primaryKey: 'id',
        foreignKeys: [
            { column: 'order_id', referenceTable: 'orders', referenceColumn: 'id' },
            { column: 'product_id', referenceTable: 'products', referenceColumn: 'id' }
        ],
        rows: [
            [10001, 1001, 101, 1, 1299.99],
            [10002, 1002, 102, 1, 899.99],
            [10003, 1003, 104, 1, 249.99],
            [10004, 1004, 108, 1, 89.99],
            [10005, 1005, 103, 1, 199.99],
            [10006, 1005, 107, 2, 24.99],
            [10007, 1006, 106, 1, 199.99]
        ]
    }
};

// Function to convert SQL type to SQLite type
const sqlTypeToSqliteType = (sqlType: string): string => {
    switch (sqlType.toLowerCase()) {
        case 'number':
        case 'int':
        case 'integer':
        case 'decimal':
        case 'numeric':
            return 'INTEGER';
        case 'float':
        case 'real':
        case 'double':
            return 'REAL';
        case 'boolean':
        case 'bool':
            return 'INTEGER'; // SQLite doesn't have a boolean type, it uses INTEGER
        case 'date':
        case 'datetime':
        case 'timestamp':
            return 'TEXT'; // SQLite doesn't have a date type, it uses TEXT
        case 'string':
        case 'varchar':
        case 'char':
        case 'text':
        case 'enum':
            return 'TEXT';
        case 'blob':
        case 'binary':
            return 'BLOB';
        default:
            return 'TEXT';
    }
};

// Function to identify primary key columns
const identifyPrimaryKey = (table: DatabaseTable): string | null => {
    if (table.primaryKey) {
        return table.primaryKey;
    }
    
    // In our simplified model, assume columns named 'id' are primary keys
    const idColumn = table.columns.find(col => col.name.toLowerCase() === 'id');
    return idColumn ? idColumn.name : null;
};

// Function to identify foreign key relationships
const identifyForeignKeys = (tables: Record<string, DatabaseTable>): Record<string, ForeignKeyDefinition[]> => {
    const relationships: Record<string, ForeignKeyDefinition[]> = {};
    
    // Initialize relationships object
    Object.keys(tables).forEach(tableName => {
        relationships[tableName] = [];
    });
    
    // Add explicitly defined foreign keys
    Object.keys(tables).forEach(tableName => {
        const table = tables[tableName];
        
        if (table.foreignKeys && table.foreignKeys.length > 0) {
            relationships[tableName] = [...table.foreignKeys];
        }
    });
    
    // Look for potential foreign keys based on naming convention (e.g., user_id)
    Object.keys(tables).forEach(tableName => {
        const table = tables[tableName];
        
        table.columns.forEach(column => {
            // Check if we already have this as a foreign key
            if (relationships[tableName].some(fk => fk.column === column.name)) {
                return;
            }
            
            // Check if column name follows the pattern table_id
            const match = column.name.match(/^(.+)_id$/);
            if (match) {
                const referencedTableName = match[1];
                
                // Check if this is a valid table name (singular or plural form)
                let targetTable = tables[referencedTableName];
                if (!targetTable) {
                    // Try singular/plural variations
                    const singularForm = referencedTableName.endsWith('s') 
                        ? referencedTableName.slice(0, -1) 
                        : referencedTableName;
                    const pluralForm = referencedTableName.endsWith('s') 
                        ? referencedTableName 
                        : referencedTableName + 's';
                    
                    targetTable = tables[singularForm] || tables[pluralForm];
                }
                
                if (targetTable) {
                    // Found a potential foreign key relationship
                    relationships[tableName].push({
                        column: column.name,
                        referenceTable: targetTable.name,
                        referenceColumn: targetTable.primaryKey || 'id'
                    });
                }
            }
        });
    });
    
    return relationships;
};

// Parse SQL query to determine tables and columns to include in SQLite schema
const parseSqlForSchema = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic parsing for SQL statements
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // SELECT statement parsing
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();

        // Check if table exists
        if (!database[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        const table = database[tableName];

        // Parse columns
        let selectedColumns: number[] = [];
        let columnNames: string[] = [];

        if (columns === '*') {
            // Select all columns
            selectedColumns = table.columns.map((_, index) => index);
            columnNames = table.columns.map(col => col.name);
        } else {
            // Select specific columns
            const colList = columns.split(',').map(col => col.trim());

            colList.forEach(colName => {
                const colIndex = table.columns.findIndex(col => col.name === colName);
                if (colIndex === -1) {
                    throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
                }
                selectedColumns.push(colIndex);
                columnNames.push(colName);
            });
        }

        return {
            tableName,
            columnNames,
            columnIndices: selectedColumns,
            fullTable: table,
        };
    } else if (sqlQuery.toLowerCase().startsWith('create table')) {
        // CREATE TABLE statement parsing (simplified)
        const createMatch = sqlQuery.match(/create\s+table\s+(\w+)\s*\(([\s\S]*)\);?\s*$/i);
        
        if (!createMatch) {
            throw new Error('Invalid CREATE TABLE syntax. Expected: CREATE TABLE table_name (column definitions);');
        }
        
        const tableName = createMatch[1].trim();
        const columnDefinitions = createMatch[2].trim();
        
        // Parse column definitions
        const columnLines = columnDefinitions.split(',').map(line => line.trim());
        const columns: ColumnDefinition[] = [];
        
        columnLines.forEach(line => {
            const colMatch = line.match(/(\w+)\s+(\w+)(?:\s+(.*))?/);
            if (colMatch) {
                const columnName = colMatch[1];
                const columnType = colMatch[2];
                const constraints = colMatch[3] ? colMatch[3].split(/\s+/) : [];
                
                columns.push({
                    name: columnName,
                    type: columnType,
                    constraints: constraints
                });
            }
        });
        
        // Create a mock table for this CREATE TABLE statement
        const createdTable: DatabaseTable = {
            name: tableName,
            columns: columns,
            rows: []
        };
        
        return {
            tableName,
            columnNames: columns.map(col => col.name),
            columnIndices: columns.map((_, index) => index),
            fullTable: createdTable,
        };
    } else {
        throw new Error('Only SELECT and CREATE TABLE queries are supported for SQLite schema generation');
    }
};

// Generate SQLite CREATE TABLE statement
const generateSqliteSchema = (
    sqlAnalysis: { 
        tableName: string; 
        columnNames: string[]; 
        columnIndices: number[]; 
        fullTable: DatabaseTable 
    },
    database: Record<string, DatabaseTable>,
    options: {
        includeForeignKeys: boolean;
        includeIndexes: boolean;
        includeTriggers: boolean;
    }
): string => {
    const { tableName, columnNames, columnIndices, fullTable } = sqlAnalysis;
    const { includeForeignKeys, includeIndexes, includeTriggers } = options;
    
    // Identify primary key
    const primaryKey = identifyPrimaryKey(fullTable);
    
    // Identify foreign key relationships if needed
    const relationships = includeForeignKeys ? identifyForeignKeys(database) : {};
    
    // Build the SQLite schema
    let schema = `-- SQLite schema generated from SQL query\n\n`;
    
    // Create table statement
    schema += `CREATE TABLE ${tableName} (\n`;
    
    // Add column definitions
    const columnDefs: string[] = [];
    
    for (let i = 0; i < columnNames.length; i++) {
        const colIndex = columnIndices[i];
        const column = fullTable.columns[colIndex];
        const sqliteType = sqlTypeToSqliteType(column.type);
        
        let columnDef = `  ${column.name} ${sqliteType}`;
        
        // Add constraints if any
        if (column.constraints && column.constraints.length > 0) {
            columnDef += ` ${column.constraints.join(' ')}`;
        } else if (column.name === primaryKey) {
            columnDef += ` PRIMARY KEY`;
            if (sqliteType === 'INTEGER') {
                columnDef += ` AUTOINCREMENT`;
            }
        }
        
        columnDefs.push(columnDef);
    }
    
    // Add foreign key constraints if enabled
    if (includeForeignKeys && relationships[tableName] && relationships[tableName].length > 0) {
        relationships[tableName].forEach(fk => {
            // Check if the column exists in our selected columns
            if (columnNames.includes(fk.column)) {
                columnDefs.push(`  FOREIGN KEY (${fk.column}) REFERENCES ${fk.referenceTable}(${fk.referenceColumn})`);
            }
        });
    }
    
    schema += columnDefs.join(',\n');
    schema += `\n);\n\n`;
    
    // Add indexes if enabled
    if (includeIndexes) {
        // Create an index on primary key if it exists
        if (primaryKey) {
            schema += `-- Index on primary key\n`;
            schema += `CREATE UNIQUE INDEX idx_${tableName}_${primaryKey} ON ${tableName} (${primaryKey});\n\n`;
        }
        
        // Create indexes on foreign keys
        if (includeForeignKeys && relationships[tableName] && relationships[tableName].length > 0) {
            schema += `-- Indexes on foreign keys\n`;
            relationships[tableName].forEach(fk => {
                if (columnNames.includes(fk.column)) {
                    schema += `CREATE INDEX idx_${tableName}_${fk.column} ON ${tableName} (${fk.column});\n`;
                }
            });
            schema += '\n';
        }
    }
    
    // Add triggers if enabled
    if (includeTriggers) {
        schema += `-- Example triggers\n`;
        
        // Add an update timestamp trigger as an example
        const hasCreatedAt = columnNames.includes('created_at');
        const hasUpdatedAt = columnNames.includes('updated_at');
        
        if (hasUpdatedAt) {
            schema += `-- Update the updated_at timestamp when a row is updated\n`;
            schema += `CREATE TRIGGER trg_${tableName}_updated_at\n`;
            schema += `AFTER UPDATE ON ${tableName}\n`;
            schema += `FOR EACH ROW\n`;
            schema += `BEGIN\n`;
            schema += `  UPDATE ${tableName} SET updated_at = CURRENT_TIMESTAMP WHERE ${primaryKey || 'id'} = NEW.${primaryKey || 'id'};\n`;
            schema += `END;\n\n`;
        }
        
        // Add a trigger for inserting default values
        if (hasCreatedAt || hasUpdatedAt) {
            schema += `-- Set default timestamps when a new row is inserted\n`;
            schema += `CREATE TRIGGER trg_${tableName}_insert_timestamps\n`;
            schema += `BEFORE INSERT ON ${tableName}\n`;
            schema += `FOR EACH ROW\n`;
            schema += `BEGIN\n`;
            if (hasCreatedAt) {
                schema += `  UPDATE ${tableName} SET created_at = CURRENT_TIMESTAMP WHERE ${primaryKey || 'id'} = NEW.${primaryKey || 'id'};\n`;
            }
            if (hasUpdatedAt) {
                schema += `  UPDATE ${tableName} SET updated_at = CURRENT_TIMESTAMP WHERE ${primaryKey || 'id'} = NEW.${primaryKey || 'id'};\n`;
            }
            schema += `END;\n\n`;
        }
    }
    
    // Add INSERT statements for sample data
    schema += `-- Sample INSERT statements\n`;
    
    fullTable.rows.slice(0, 5).forEach(row => {
        const values = row.map((value, index) => {
            const colIndex = columnIndices[index];
            const column = fullTable.columns[colIndex];
            
            // Format the value based on its type
            if (column.type === 'string' || column.type === 'date') {
                return `'${value}'`;
            } else if (column.type === 'boolean') {
                return value ? '1' : '0';
            } else {
                return value;
            }
        });
        
        schema += `INSERT INTO ${tableName} (${columnNames.join(', ')}) VALUES (${values.join(', ')});\n`;
    });
    
    // Sample PRAGMA statements
    schema += `\n-- Recommended PRAGMA statements for SQLite performance\n`;
    schema += `PRAGMA journal_mode = WAL;\n`;
    schema += `PRAGMA synchronous = NORMAL;\n`;
    schema += `PRAGMA foreign_keys = ON;\n`;
    
    return schema;
};

// Generate sample queries for created schema
const generateSampleQueries = (
    sqlAnalysis: { 
        tableName: string; 
        columnNames: string[]; 
        columnIndices: number[]; 
        fullTable: DatabaseTable 
    },
    database: Record<string, DatabaseTable>
): string => {
    const { tableName, columnNames, fullTable } = sqlAnalysis;
    
    // Identify primary key
    const primaryKey = identifyPrimaryKey(fullTable);
    
    // Identify foreign key relationships
    const relationships = identifyForeignKeys(database);
    
    let queries = `-- Sample SQLite queries for ${tableName}\n\n`;
    
    // SELECT query
    queries += `-- Select all records\n`;
    queries += `SELECT * FROM ${tableName};\n\n`;
    
    // SELECT with WHERE
    if (primaryKey) {
        queries += `-- Select by primary key\n`;
        queries += `SELECT * FROM ${tableName} WHERE ${primaryKey} = 1;\n\n`;
    }
    
    // SELECT with JOIN if we have foreign keys
    if (relationships[tableName] && relationships[tableName].length > 0) {
        const fk = relationships[tableName][0];
        queries += `-- Select with JOIN on foreign key\n`;
        queries += `SELECT ${tableName}.*, ${fk.referenceTable}.* FROM ${tableName}\n`;
        queries += `JOIN ${fk.referenceTable} ON ${tableName}.${fk.column} = ${fk.referenceTable}.${fk.referenceColumn};\n\n`;
    }
    
    // INSERT query
    queries += `-- Insert a new record\n`;
    queries += `INSERT INTO ${tableName} (${columnNames.filter(name => name !== primaryKey).join(', ')})\n`;
    queries += `VALUES (/* values go here */);\n\n`;
    
    // UPDATE query
    if (primaryKey) {
        queries += `-- Update a record\n`;
        queries += `UPDATE ${tableName}\n`;
        queries += `SET ${columnNames[0]} = 'new value'\n`;
        queries += `WHERE ${primaryKey} = 1;\n\n`;
    }
    
    // DELETE query
    if (primaryKey) {
        queries += `-- Delete a record\n`;
        queries += `DELETE FROM ${tableName}\n`;
        queries += `WHERE ${primaryKey} = 1;\n\n`;
    }
    
    // Complex query example
    queries += `-- Complex query example\n`;
    queries += `SELECT ${tableName}.${columnNames[0]}, COUNT(*) as count\n`;
    queries += `FROM ${tableName}\n`;
    if (columnNames.includes('created_at')) {
        queries += `WHERE ${tableName}.created_at > '2023-01-01'\n`;
    }
    queries += `GROUP BY ${tableName}.${columnNames[0]}\n`;
    queries += `ORDER BY count DESC\n`;
    queries += `LIMIT 10;\n`;
    
    return queries;
};

// Main component to convert SQL to SQLite Schema
const SqlToSqlite: React.FC<SqlToSqliteProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the SQLite schema output
    const [sqliteSchema, setSqliteSchema] = useState<string>('');
    // State for sample queries output
    const [sampleQueries, setSampleQueries] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for the file name
    const [fileName, setFileName] = useState<string>('schema');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isSchemaGenerated, setIsSchemaGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);
    // State for copy type (schema or queries)
    const [copyType, setCopyType] = useState<string>('');

    // SQLite generation options
    const [includeForeignKeys, setIncludeForeignKeys] = useState<boolean>(true);
    const [includeIndexes, setIncludeIndexes] = useState<boolean>(true);
    const [includeTriggers, setIncludeTriggers] = useState<boolean>(false);
    const [showSampleQueries, setShowSampleQueries] = useState<boolean>(true);

    // Handle SQL input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSqlInput(e.target.value);
        setError('');
        setIsSchemaGenerated(false);
    };

    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM users;';
        setSqlInput(exampleQuery);
        setError('');
        setIsSchemaGenerated(false);
        setSqliteSchema('');
        setSampleQueries('');
    };

    // Use example CREATE TABLE query
    const useCreateTableExample = () => {
        const exampleQuery = `CREATE TABLE customers (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  name TEXT NOT NULL,
  email TEXT UNIQUE,
  phone TEXT,
  address TEXT,
  created_at TEXT DEFAULT CURRENT_TIMESTAMP,
  updated_at TEXT
);`;
        setSqlInput(exampleQuery);
        setError('');
        setIsSchemaGenerated(false);
        setSqliteSchema('');
        setSampleQueries('');
    };

    // Convert SQL to SQLite Schema
    const convertSqlToSqlite = () => {
        setError('');
        setIsLoading(true);
        setIsSchemaGenerated(false);
        setSqliteSchema('');
        setSampleQueries('');

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query and analyze it for schema generation
            const sqlAnalysis = parseSqlForSchema(sqlInput, mockDatabase);

            // Generate the SQLite schema
            const options = {
                includeForeignKeys,
                includeIndexes,
                includeTriggers
            };

            const schema = generateSqliteSchema(sqlAnalysis, mockDatabase, options);
            setSqliteSchema(schema);

            // Generate sample queries if enabled
            if (showSampleQueries) {
                const queries = generateSampleQueries(sqlAnalysis, mockDatabase);
                setSampleQueries(queries);
            } else {
                setSampleQueries('');
            }

            setIsSchemaGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting SQL to SQLite schema: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setSqliteSchema('');
        setSampleQueries('');
        setError('');
        setFileName('schema');
        setIsSchemaGenerated(false);
    };

    // Copy to clipboard
    const copyToClipboard = (type: 'schema' | 'queries') => {
        const contentToCopy = type === 'schema' ? sqliteSchema : sampleQueries;
        navigator.clipboard.writeText(contentToCopy)
            .then(() => {
                setCopied(true);
                setCopyType(type);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download schema
    const downloadSchema = (type: 'schema' | 'queries') => {
        const content = type === 'schema' ? sqliteSchema : sampleQueries;
        const extension = '.sql';
        const prefix = type === 'schema' ? '' : 'queries_';
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${prefix}${fileName || 'schema'}${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.columns.map((col, index) => (
                                <div key={index}>
                                    {col.name} ({col.type}{col.constraints?.length ? ` ${col.constraints.join(' ')}` : ''})
                                </div>
                            ))}
                            {table.foreignKeys && table.foreignKeys.length > 0 && (
                                <div className="mt-1">
                                    <div className="font-medium text-sm text-blue-700">Foreign Keys:</div>
                                    {table.foreignKeys.map((fk, fkIndex) => (
                                        <div key={fkIndex} className="text-xs ml-2">
                                            {fk.column} → {fk.referenceTable}({fk.referenceColumn})
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database or database schema file.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to SQLite Schema Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert SQL queries to SQLite compatible schema definitions. This tool analyzes your SQL queries and database structure to generate SQLite CREATE TABLE statements, indexes, triggers, and sample queries.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-green-600 hover:text-green-800"
                                    onClick={useCreateTableExample}
                                >
                                    CREATE TABLE Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-48 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={handleInputChange}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users;
- or -
CREATE TABLE users (...);"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                                Output File Name
                            </label>
                            <input
                                type="text"
                                id="fileName"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter file name (without extension)"
                            />
                        </div>

                        <div className="space-y-3 p-3 bg-gray-50 rounded-md mb-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                SQLite Schema Options
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeForeignKeys"
                                    checked={includeForeignKeys}
                                    onChange={(e) => setIncludeForeignKeys(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeForeignKeys" className="ml-2 block text-sm text-gray-700">
                                    Include Foreign Key Constraints
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeIndexes"
                                    checked={includeIndexes}
                                    onChange={(e) => setIncludeIndexes(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeIndexes" className="ml-2 block text-sm text-gray-700">
                                    Include Indexes
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeTriggers"
                                    checked={includeTriggers}
                                    onChange={(e) => setIncludeTriggers(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeTriggers" className="ml-2 block text-sm text-gray-700">
                                    Include Example Triggers
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showSampleQueries"
                                    checked={showSampleQueries}
                                    onChange={(e) => setShowSampleQueries(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="showSampleQueries" className="ml-2 block text-sm text-gray-700">
                                    Generate Sample Queries
                                </label>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertSqlToSqlite}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Generate SQLite Schema'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            SQLite Schema Output
                        </label>

                        {isSchemaGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={() => copyToClipboard('schema')}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied && copyType === 'schema' ? 'Copied!' : 'Copy Schema'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={() => downloadSchema('schema')}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isSchemaGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p>
                                        SQLite schema will appear here after analyzing the SQL query.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isSchemaGenerated && (
                            <div className="flex-grow flex flex-col">
                                <div className="flex-grow flex flex-col">
                                    <textarea
                                        id="sqliteOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                        value={sqliteSchema}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sample Queries Section (conditionally rendered) */}
                    {showSampleQueries && isSchemaGenerated && sampleQueries && (
                        <div className="mt-6">
                            <div className="flex justify-between items-center mb-3">
                                <label className="font-medium text-gray-700">
                                    Sample SQLite Queries
                                </label>

                                <div className="flex space-x-2">
                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => copyToClipboard('queries')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {copied && copyType === 'queries' ? 'Copied!' : 'Copy Queries'}
                                    </button>

                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => downloadSchema('queries')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        Download
                                    </button>
                                </div>
                            </div>

                            <textarea
                                id="queriesOutput"
                                className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                value={sampleQueries}
                                readOnly
                            />
                        </div>
                    )}

                    {isSchemaGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQLite Schema Successfully Generated!</p>
                            </div>
                            <p className="text-sm">
                                This schema includes SQLite CREATE TABLE statements, indexes, and constraints derived from your SQL query and database structure.
                                {showSampleQueries && sampleQueries && " Sample queries have also been generated to help you get started with the schema."}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About SQL to SQLite Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
                        <p className="text-gray-600">
                            This SQL to SQLite schema converter analyzes your SQL queries and database structure to generate SQLite-compatible schema definitions. It creates CREATE TABLE statements, indexes, constraints, and sample queries based on the tables and relationships detected in your database.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Table Schema Generation</strong> - Creates SQLite table definitions from SQL queries</li>
                            <li><strong>Data Type Conversion</strong> - Maps SQL data types to SQLite-compatible types</li>
                            <li><strong>Constraint Detection</strong> - Identifies and includes primary keys, foreign keys, and unique constraints</li>
                            <li><strong>Index Generation</strong> - Creates indexes for primary and foreign keys</li>
                            <li><strong>Trigger Examples</strong> - Generates example triggers for common operations</li>
                            <li><strong>Sample Query Generation</strong> - Creates example SELECT, INSERT, UPDATE, and DELETE queries</li>
                            <li><strong>Schema Download</strong> - Download the generated schema as a .sql file</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">How It Works</h3>
                        <ol className="list-decimal pl-5 text-gray-600">
                            <li>Enter an SQL query - Use a SELECT or CREATE TABLE statement to specify the tables and columns</li>
                            <li>Configure options - Choose which SQLite features to include</li>
                            <li>Generate the schema - The tool analyzes your query and database structure</li>
                            <li>Review and customize - Examine the generated schema and sample queries</li>
                            <li>Download or copy - Save the schema for use in your SQLite database</li>
                        </ol>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">SQLite-Specific Considerations</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Data Types</strong> - SQLite uses a dynamic typing system with only a few storage classes (NULL, INTEGER, REAL, TEXT, BLOB)</li>
                            <li><strong>PRAGMA Statements</strong> - SQLite uses PRAGMA statements for database configuration</li>
                            <li><strong>Foreign Keys</strong> - Foreign key constraints must be explicitly enabled using PRAGMA foreign_keys = ON</li>
                            <li><strong>Transaction Support</strong> - SQLite is ACID-compliant and supports transactions</li>
                            <li><strong>Performance Tuning</strong> - The WAL journal mode can improve concurrency and performance</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined data</li>
                            <li>Only supports basic SELECT and CREATE TABLE queries</li>
                            <li>Simplified relationship detection based on naming conventions</li>
                            <li>Basic data type conversion from SQL to SQLite</li>
                            <li>Generated sample queries are examples and might need customization</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Using the Generated Schema</h3>
                        <p className="text-gray-600">
                            To use the generated schema with SQLite:
                        </p>
                        <ol className="list-decimal pl-5 text-gray-600">
                            <li>Save the schema to a .sql file</li>
                            <li>Run it with the SQLite command-line tool: sqlite3 database.db </li>
                            <li>Alternatively, use a SQLite GUI tool like DB Browser for SQLite to import the schema</li>
                            <li>Test the generated schema with the sample queries</li>
                            <li>Adapt and extend the schema as needed for your application</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToSqlite;